import { useRef } from 'react';
import gsap from 'gsap';
import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect';
import Arrow from '@/assets/svg/arrow-right-light.svg';
import styles from './CampaignComparison.module.scss';

export type CampaignComparisonItem = { id: string; from: string; to: string };

export default function CampaignComparison({
  items,
  label,
  animate = true,
}: {
  items: CampaignComparisonItem[];
  label: string;
  animate?: boolean;
}) {
  const ref = useRef<HTMLUListElement>(null);
  useIsomorphicLayoutEffect(() => {
    const element = ref.current;
    if (!element || !animate || !('IntersectionObserver' in window)) return;
    const media = gsap.matchMedia();
    media.add('(prefers-reduced-motion: no-preference)', () => {
      const timeline = gsap.timeline({ paused: true });
      const rowStagger = 0.6;
      const itemStagger = 0.2;
      Array.from(element.children).forEach((row, index) => {
        const rowStart = index * rowStagger;
        timeline
          .fromTo(
            row.querySelector('[data-comparison="from"]'),
            { opacity: 0, x: -20 },
            { opacity: 1, x: 0, duration: 0.5, ease: 'power2.out' },
            rowStart,
          )
          .fromTo(
            row.querySelector('[data-comparison="arrow"]'),
            { opacity: 0, x: -8 },
            { opacity: 1, x: 0, duration: 0.3, ease: 'power2.out' },
            rowStart + itemStagger,
          )
          .fromTo(
            row.querySelector('[data-comparison="to"]'),
            { opacity: 0, x: -16 },
            { opacity: 1, x: 0, duration: 0.5, ease: 'power2.out' },
            rowStart + itemStagger * 2,
          );
      });
      timeline.set(element.querySelectorAll('[data-comparison]'), {
        clearProps: 'transform,opacity',
      });
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          timeline.play();
        },
        { threshold: 0.25 },
      );
      observer.observe(element);
      return () => {
        observer.disconnect();
        timeline.revert();
      };
    });
    return () => media.revert();
  }, [items, animate]);

  if (!items.length) return null;
  return (
    <div className={styles.container}>
      <ul ref={ref} className={styles.comparison} aria-label={label}>
        {items.map((item) => (
          <li key={item.id} className={styles.row}>
            <span data-comparison="from" className={styles.from}>
              {item.from}
            </span>
            <span data-comparison="arrow" className={styles.arrow}>
              <Arrow aria-hidden="true" focusable="false" />
              <span className={styles.srOnly}>to</span>
            </span>
            <span data-comparison="to" className={styles.to}>
              {item.to}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
